import { useRef, ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StickerConfig, Shape } from "@/lib/sticker";
import { Trash, Upload, Square, Circle, Triangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/lib/i18n";

interface StickerPreviewAreaProps {
  stickerConfig: StickerConfig;
  onImageUpload: (file: File) => void;
  onRemoveImage: () => void;
}

export const StickerPreviewArea = ({
  stickerConfig,
  onImageUpload,
  onRemoveImage,
}: StickerPreviewAreaProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const t = useTranslation();

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onImageUpload(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // 根据形状生成裁剪样式
  const getShapeClipPath = (shape: Shape) => {
    switch (shape) {
      case "circle":
        return "circle(50%)";
      case "rectangle":
        return "none";
      case "contour":
        return "none";
      default:
        return "none";
    }
  };

  // 获取形状的边框半径
  const getShapeBorderRadius = (shape: Shape) => {
    switch (shape) {
      case "circle":
        return "50%";
      case "rectangle":
        return "8px";
      case "contour":
        return "16px";
      default:
        return "8px";
    }
  };

  // 计算贴纸在正方形容器内的尺寸和位置
  const getStickerDimensions = () => {
    const { size } = stickerConfig;
    const maxSize = 80; // 80% of container to leave some padding

    if (stickerConfig.shape === "circle") {
      // 圆形贴纸始终保持正方形
      return {
        width: maxSize,
        height: maxSize,
        marginTop: (100 - maxSize) / 2,
        marginLeft: (100 - maxSize) / 2,
      };
    }

    // 矩形和轮廓贴纸根据宽高比例计算
    const ratio = size.width / size.height;

    let width: number;
    let height: number;

    if (ratio > 1) {
      // 宽度大于高度，以宽度为基准
      width = maxSize;
      height = maxSize / ratio;
    } else {
      // 高度大于等于宽度，以高度为基准
      width = maxSize * ratio;
      height = maxSize;
    }

    // 计算居中位置
    const marginTop = (100 - height) / 2;
    const marginLeft = (100 - width) / 2;

    return {
      width,
      height,
      marginTop,
      marginLeft,
    };
  };

  const stickerDimensions = getStickerDimensions();

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <div className="text-sm font-medium text-foreground mb-3">
          {t.preview.title}
        </div>

        <Input
          id="custom-image"
          type="file"
          className="hidden"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
        />

        {/* 正方形预览区域 */}
        <AspectRatio ratio={1} className="relative overflow-hidden">
          <div
            className="absolute inset-0 rounded-lg border-2 border-dashed border-border/60"
            style={{
              backgroundImage: `
                linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
                linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
                linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
              `,
              backgroundSize: "12px 12px",
              backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0px",
              backgroundColor: "white",
            }}
          >
            {stickerConfig.image ? (
              <>
                {/* 贴纸图片 - 根据配置比例显示 */}
                <div
                  className="absolute"
                  style={{
                    width: `${stickerDimensions.width}%`,
                    height: `${stickerDimensions.height}%`,
                    top: `${stickerDimensions.marginTop}%`,
                    left: `${stickerDimensions.marginLeft}%`,
                    clipPath: getShapeClipPath(stickerConfig.shape),
                    borderRadius: getShapeBorderRadius(stickerConfig.shape),
                  }}
                >
                  <img
                    src={stickerConfig.image}
                    alt="Sticker preview"
                    className="w-full h-full object-cover"
                  />

                  {/* 材质效果叠加 */}
                  {stickerConfig.material === "glossy" && (
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />
                  )}
                  {stickerConfig.material === "matte" && (
                    <div className="absolute inset-0 bg-black/5 pointer-events-none" />
                  )}
                  {stickerConfig.material === "translucent" && (
                    <div className="absolute inset-0 bg-white/10 backdrop-blur-[0.5px] pointer-events-none" />
                  )}
                </div>

                {/* 底部白色半透明高斯模糊蒙版 */}
                <div className="absolute bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-t border-white/20 p-3 rounded-b-lg">
                  <div className="flex items-center justify-between">
                    {/* 左侧：尺寸信息 */}
                    <div className="flex-1">
                      <div className="text-xs text-gray-800 font-medium">
                        {stickerConfig.shape === "circle"
                          ? `⌀ ${stickerConfig.size.radius} ${stickerConfig.unit}`
                          : `${stickerConfig.size.width} × ${stickerConfig.size.height} ${stickerConfig.unit}`}
                      </div>
                      <div className="text-[0.6rem] text-gray-600 mt-0.5">
                        {stickerConfig.shape.toUpperCase()} •{" "}
                        {stickerConfig.material.toUpperCase()}
                      </div>
                    </div>

                    {/* 右侧：操作按钮 */}
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleUploadClick}
                            className="h-8 w-8 p-0 hover:bg-gray-100 text-gray-700 hover:text-gray-900 cursor-pointer"
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t.common.replace}</p>
                        </TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={onRemoveImage}
                            className="h-8 w-8 p-0 hover:bg-gray-100 text-gray-700 hover:text-gray-900 cursor-pointer"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t.common.remove}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* 空状态 */
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-muted/20 rounded-full flex items-center justify-center">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground mb-1">
                      {t.preview.uploadTitle}
                    </div>
                    <div className="text-xs text-muted-foreground mb-3">
                      {t.preview.fileTypes}
                    </div>
                    <Button
                      onClick={handleUploadClick}
                      className="text-xs h-8 px-4 cursor-pointer"
                    >
                      {t.preview.chooseFile}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </AspectRatio>

        {/* 形状指示器 */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Square
            className={cn(
              "w-3 h-3",
              stickerConfig.shape === "rectangle"
                ? "text-current"
                : "text-muted-foreground/50",
            )}
          />
          <Circle
            className={cn(
              "w-3 h-3",
              stickerConfig.shape === "circle"
                ? "text-current"
                : "text-muted-foreground/50",
            )}
          />
          <Triangle
            className={cn(
              "w-3 h-3",
              stickerConfig.shape === "contour"
                ? "text-current"
                : "text-muted-foreground/50",
            )}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};
